import { useEffect, useRef, useState } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';
import {
  useCaptionProgressMutation,
  useGeneratePreSignedUrl,
  usePresignedUrlUpload,
  useUploadId,
} from '../../services';
import { useCreateBrollVideo } from '../../services/broll-videos/createVideo';
import { getRandomInt, isInputEmpty, VIDEO_DEFAULT_NAME_PREFIX } from '../../utils';
import { MediaStatus } from '../../utils/type';
import { useAppContext } from '../context';
import { Status } from '../../@types';
import { useGetBrollCaptionProgress } from './useGetBrollCaptionProgress';

export enum CreateBrollCaptionTabs {
  Upload = 'Upload',
  SelectVideo = 'SelectVideo',
}

interface FormValuesProps {
  videoFile: File | null;
  videoName: string;
}

interface CreateBrollCaptionProps {
  isOpen: boolean;
  closeCreateBrollCaptionDialog: () => void;
  refetchBrollAndCaptionData: () => void;
}

export const useCreateBrollCaption = ({
  isOpen,
  refetchBrollAndCaptionData,
  closeCreateBrollCaptionDialog,
}: CreateBrollCaptionProps) => {
  const [currentTab, setCurrentTab] = useState<CreateBrollCaptionTabs>(
    CreateBrollCaptionTabs.Upload
  );
  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [overallPercentage, setOverallPercentage] = useState(0);
  const abortController = useRef<AbortController | null>(null);
  const [uploadId, setUploadId] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const [isProcessingVideo, setIsProcessingVideo] = useState(false);
  const [isUploadError, setIsUploadError] = useState(false);
  const [isRetryDisable, setIsRetryDisable] = useState(false);
  const {
    websocket: { sendMessage },
  } = useAppContext();

  const { videoDataStatus } = useGetBrollCaptionProgress({
    id: uploadId,
    uploadPercentage,
    setOverallPercentage,
    refreshBrollData: refetchBrollAndCaptionData,
  });

  const formMethods = useForm<FormValuesProps>({
    resolver: yupResolver(
      Yup.object().shape({
        videoFile: Yup.mixed().required('Video file is required'),
        videoName: Yup.string().required('Video name is required'),
      })
    ),
    defaultValues: {
      videoFile: null,
    },
  });

  const { handleSubmit, setValue, getValues } = formMethods;

  const { mutateAsync: generatePreSignedUrl } = useGeneratePreSignedUrl();
  const { mutateAsync: presignedUrlUpload } = usePresignedUrlUpload({
    onUploadProgress: (percentCompleted) => {
      setUploadPercentage(percentCompleted);
    },
  });
  const { mutateAsync: fetchUploadId } = useUploadId();
  const { mutateAsync: createVideo } = useCreateBrollVideo();

  const startUpload = async (videoFile: File, videoName: string) => {
    abortController.current = new AbortController();
    const { signal } = abortController.current;

    try {
      const { uploadId: multiPartUploadId, preSignedUrls, format } = await generatePreSignedUrl(videoFile);
      await presignedUrlUpload({ uploadId: multiPartUploadId, presignedUrls: preSignedUrls, file: videoFile, signal });

      const uploadResponse = await fetchUploadId({
        url: preSignedUrls[0].url,
        format,
        mimeType: videoFile.type,
        signal,
      });

      await createVideo({ uploadId: uploadResponse.uploadId, name: videoName });
      sendMessage({
        event: 'register',
        data: {
          id: uploadResponse.uploadId,
        },
      });
      setUploadId(uploadResponse.uploadId);
      refetchBrollAndCaptionData();
    } catch (error) {
      // setIsUploadError(true);
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        enqueueSnackbar('Upload cancelled.', { variant: 'info' });
      } else {
        setIsUploadError(true);
      }
    } finally {
      abortController.current = null;
    }
  };

  const onSubmit: SubmitHandler<FormValuesProps> = async ({ videoFile, videoName }) => {
    if (!videoName || isInputEmpty(videoName)) {
      enqueueSnackbar('Please enter the video name', { variant: 'error' });
      return;
    }
    setIsProcessingVideo(true);
    setUploadPercentage(0);
    setIsUploadError(false);
    await startUpload(videoFile, videoName);
  };

  useEffect(() => {
    if (!isOpen) setValue('videoFile', null);
    if (isOpen) setValue('videoName', VIDEO_DEFAULT_NAME_PREFIX + getRandomInt(1, 1000));
  }, [isOpen, setValue]);

  const handleOnRetry = () => {
    setIsRetryDisable(true);
    setIsProcessingVideo(true);
    setUploadPercentage(0);
    setOverallPercentage(0);
    setIsUploadError(false);
    setUploadId('');
    startUpload(getValues('videoFile'), getValues('videoName'));
  };

  return {
    handleSubmit: handleSubmit(onSubmit),
    formMethods,
    currentTab,
    handleTabChange: setCurrentTab,
    handleCloseModal: () => {
      abortController.current?.abort();
      closeCreateBrollCaptionDialog();
    },
    overallPercentage: Math.round(overallPercentage),
    uploadId,
    isProcessingVideo,
    videoDataStatus,
    handleOnRetry,
    isUploadError,
    isRetryDisable,
  };
};
